/**
 * dashboardViews.jsx — registry of drill-down tables shown by DashboardDataView.
 *
 * Each dashboard links to `/dashboard/view/<key>?<params>`; the entry under
 * that key decides where the rows come from and which columns to show, so
 * every click-through gets a table shaped for what was clicked.
 *
 * Entry shape:
 *   title(params, meta)     → header text
 *   subtitle(params, meta)  → optional muted line under the title
 *   icon                    → header icon
 *   load({ params, token }) → { rows, meta } | { denied } | { error }
 *   rowKey(row)             → stable key per row
 *   rowLink(row, ctx)       → optional path to open on row click (ctx: { canAccessPage })
 *   columns: [{
 *     key, label,
 *     value(row)    → raw value used for sorting / totals (defaults to row[key])
 *     render(row)   → cell content (defaults to value)
 *     sortable      → header click sorts client-side
 *     align         → 'left' | 'right' | 'center'
 *     total         → footer sums value(row); `format` renders the sum
 *     mobileHide    → hidden on small screens like the Enquiry table
 *   }]
 */

import React from 'react';
import { FiDollarSign, FiTrendingUp } from 'react-icons/fi';
import { apiRequest } from '../utils/apiRequest';
import { API_URLS } from '../config';

const formatINR = (n) => `₹${Math.round(Number(n || 0)).toLocaleString('en-IN')}`;

const formatMonthLabel = (ym) => {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const QUARTER_MONTHS = { 1: 'Apr–Jun', 2: 'Jul–Sep', 3: 'Oct–Dec', 4: 'Jan–Mar' };
const formatQuarterLabel = (key) => {
  const match = /^FY(\d{4})-Q([1-4])$/.exec(key || '');
  if (!match) return '';
  const fy = Number(match[1]);
  return `Q${match[2]} FY ${fy}-${String(fy + 1).slice(2)} (${QUARTER_MONTHS[match[2]]})`;
};

const BUCKET_LABEL = { INDIA: 'India', OTHERS: 'Overseas' };
const typeLabel = (t) => (t === 'MANMONTH' ? 'Manmonth' : 'Manday');

// Enquiry rows open the Manday edit form when the user may see enquiries.
const enquiryLink = (row, { canAccessPage }) =>
  row.enquiryId && canAccessPage('enquiry_register') ? `/enquiry/manday/${row.enquiryId}` : null;

// Both incentive endpoints return every coordinator; pick the one clicked.
const loadCoordinator = async (path, periodParam, params, token) => {
  const query = new URLSearchParams({ [periodParam]: params.get(periodParam) || '' });
  if (params.get('companyId')) query.set('companyId', params.get('companyId'));
  // Quarterly view narrowed to one month of the quarter.
  if (periodParam === 'quarter' && params.get('month')) query.set('month', params.get('month'));
  const res = await apiRequest(`${API_URLS.INCENTIVE}${path}?${query}`, { method: 'GET' }, token);
  if (!res) return { error: 'Request failed' };
  if (res.denied) return { denied: res };
  if (!res.ok) return { error: res.data?.message || 'Failed to load data' };
  const data = res.data?.data || {};
  // Admin/finance responses include a `coordinators` array; coordinator
  // self-view returns a single object (data IS the coordinator).
  const coordinator = data.coordinators
    ? data.coordinators.find((c) => c.coordinatorId === params.get('coordinatorId'))
    : data;
  return { coordinator, data };
};

const eligiblePill = (eligible, text) => (
  <span className={`home-pill ${eligible ? 'home-pill-good' : 'home-pill-warn'}`}>{text}</span>
);

const DASHBOARD_VIEWS = {
  // Home → monthly coordinator incentive → coordinator name
  'coordinator-incentive': {
    icon: <FiDollarSign size={18} />,
    title: (params, meta) => `Monthly Incentive - ${meta?.name || params.get('name') || 'Coordinator'}`,
    subtitle: (params, meta) => (
      <>
        {formatMonthLabel(params.get('month'))}
        {meta && <> · Won {meta.wonCount} of {meta.floor} needed · {eligiblePill(meta.eligible, meta.eligible ? 'Eligible' : 'Not eligible')} · Total {formatINR(meta.total)}</>}
      </>
    ),
    load: async ({ params, token }) => {
      const { coordinator, data, ...rest } = await loadCoordinator('/coordinator', 'month', params, token);
      if (!data) return rest;
      return { rows: coordinator?.details || [], meta: coordinator || null };
    },
    rowKey: (r) => r.enquiryId || r.jobNumber,
    rowLink: enquiryLink,
    emptyText: 'No won enquiries this month.',
    columns: [
      { key: 'jobNumber', label: 'JOB NUMBER', sortable: true },
      { key: 'enquiryType', label: 'TYPE', sortable: true, render: (r) => typeLabel(r.enquiryType) },
      { key: 'bucket', label: 'COUNTRY', sortable: true, render: (r) => BUCKET_LABEL[r.bucket] || r.bucket },
      { key: 'marginPercent', label: 'MARGIN', sortable: true, align: 'right', render: (r) => `${Number(r.marginPercent || 0).toFixed(1)}%` },
      { key: 'rate', label: 'RATE EARNED', sortable: true, align: 'right', render: (r) => formatINR(r.rate), total: true, format: formatINR },
    ],
  },

  // Home → quarterly incentive → coordinator name
  'coordinator-quarterly-incentive': {
    icon: <FiTrendingUp size={18} />,
    title: (params, meta) => `Quarterly incentive - ${meta?.name || params.get('name') || 'Coordinator'}`,
    subtitle: (params, meta) => (
      <>
        {formatQuarterLabel(params.get('quarter'))}
        {params.get('month') && <> · {formatMonthLabel(params.get('month'))}</>}
        {meta && <> · {eligiblePill(meta.eligible, meta.eligible ? 'Target met' : `${formatINR(meta.shortfall)} short`)}</>}
      </>
    ),
    load: async ({ params, token }) => {
      const { coordinator, data, ...rest } = await loadCoordinator('/coordinator/quarterly', 'quarter', params, token);
      if (!data) return rest;
      return { rows: coordinator?.enquiries || [], meta: coordinator || null };
    },
    rowKey: (r) => r.enquiryId || r.jobNumber,
    rowLink: enquiryLink,
    emptyText: 'No won inspection days or issued invoices this quarter.',
    columns: [
      { key: 'jobNumber', label: 'JOB NUMBER', sortable: true },
      { key: 'enquiryType', label: 'TYPE', sortable: true, render: (r) => typeLabel(r.enquiryType) },
      { key: 'probableRevenue', label: 'PROBABLE REVENUE', sortable: true, align: 'left', render: (r) => formatINR(r.probableRevenue), total: true, format: formatINR, totalFilter: (r) => r.quarterlyIncentiveEligible !== false },
      { key: 'invoicedRevenue', label: 'INVOICED REVENUE', sortable: true, align: 'left', render: (r) => formatINR(r.invoicedRevenue), total: true, format: formatINR, totalFilter: (r) => r.quarterlyIncentiveEligible !== false },
      {
        key: 'invoiceNumbers', label: 'INVOICE NO.', mobileHide: true,
        value: (r) => (r.invoiceNumbers || []).join(', '),
        render: (r) => (r.invoiceNumbers || []).join(', ') || <span style={{ color: '#999' }}>Not invoiced</span>,
      },
      {
        key: 'quarterlyIncentiveEligible', label: 'ELIGIBLE', sortable: true,
        value: (r) => (r.quarterlyIncentiveEligible === false ? 'Not Eligible' : 'Eligible'),
        render: (r) => eligiblePill(r.quarterlyIncentiveEligible !== false, r.quarterlyIncentiveEligible === false ? 'Not Eligible' : 'Eligible'),
      },
    ],
  },
};

export default DASHBOARD_VIEWS;
